<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">inventory_2</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Reward Inventory</h2>
          <p class="text-sm text-gray-500">Manage available reward prizes.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">redeem</span>
            <span class="text-xs font-semibold tracking-widest uppercase"
              >Rewards Inventory</span
            >
          </div>
          <router-link
            to="/rewards-inventory/add"
            class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white rounded-xl shadow-sm bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-600"
          >
            <span class="mr-1 text-base material-icons-round">add</span>
            Add Prize
          </router-link>
        </div>
      </div>

      <div
        class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Prize Item',
                    'Initial Amount in Stock',
                    'Currently in Stock',
                    '',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="rewardPrizes.length">
              <tr
                v-for="(rewardPrize, index) in rewardPrizes"
                :key="rewardPrize._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Prize item -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full text-taxiye-600 bg-taxiye-50"
                    >
                      <i class="text-lg material-icons-round">card_giftcard</i>
                    </span>
                    <p class="font-medium text-gray-900">
                      {{ rewardPrize.prizeItem }}
                    </p>
                  </div>
                </td>
                <!-- Initial stock -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ rewardPrize.initialAmountInStock }}
                </td>
                <!-- Current stock -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-md"
                    :class="stockBadgeClass(rewardPrize)"
                  >
                    {{ rewardPrize.amountInStock }} in stock
                  </span>
                  <div class="w-24 h-1.5 mt-2 bg-gray-200 rounded-full">
                    <div
                      class="h-1.5 rounded-full"
                      :class="stockBarClass(rewardPrize)"
                      :style="{ width: `${stockPercent(rewardPrize)}%` }"
                    ></div>
                  </div>
                </td>
                <!-- Actions -->
                <td
                  class="px-5 py-4 text-right whitespace-no-wrap"
                  v-click-outside="hideDropdown"
                >
                  <div @click.stop class="relative inline-block text-left">
                    <button
                      @click="toggleDropdown(index)"
                      type="button"
                      class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                      aria-haspopup="true"
                      :aria-expanded="openDropdown === index"
                    >
                      <i class="material-icons-round">more_vert</i>
                    </button>
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0"
                    >
                      <div
                        v-if="openDropdown === index"
                        class="absolute right-0 z-10 w-56 mt-1 origin-top-right bg-white border border-gray-100 shadow-lg rounded-xl"
                      >
                        <div
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="options-menu"
                          class="py-1"
                        >
                          <router-link
                            :to="`rewards-inventory/${rewardPrize._id}/edit`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">edit</i>
                            <span>Edit</span>
                          </router-link>
                        </div>
                      </div>
                    </transition>
                  </div>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin"
                  >sync</i
                >
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="4" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i
                      class="text-5xl text-taxiye-500 material-icons-round spin"
                      >sync</i
                    >
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span
                      class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100"
                    >
                      <i class="text-3xl material-icons-round">redeem</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500"
                      >No reward inventory found</span
                    >
                    <span class="mt-1 text-xs text-gray-400"
                      >Add a prize to get started</span
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationMixin from "@/mixins/Pagination";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      rewardPrizes: [],
      openDropdown: false,
      loading: true,
    };
  },
  components: {
    Pagination,
  },
  watch: {
    currentPage() {
      this.getRewardPrizes();
    },
    perPage() {
      this.currentPage === 1 ? this.getRewardPrizes() : (this.currentPage = 1);
    },
  },
  created() {
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page;
    }

    this.getRewardPrizes();
  },
  methods: {
    stockPercent(rewardPrize) {
      const initial = Number(rewardPrize.initialAmountInStock) || 0;
      const current = Number(rewardPrize.amountInStock) || 0;
      if (!initial) return 0;
      return Math.min(100, Math.max(0, (current / initial) * 100));
    },
    stockBadgeClass(rewardPrize) {
      const percent = this.stockPercent(rewardPrize);
      if (percent <= 0) return "text-red-700 bg-red-100";
      if (percent <= 25) return "text-yellow-700 bg-yellow-100";
      return "text-green-700 bg-green-100";
    },
    stockBarClass(rewardPrize) {
      const percent = this.stockPercent(rewardPrize);
      if (percent <= 0) return "bg-red-500";
      if (percent <= 25) return "bg-yellow-400";
      return "bg-green-500";
    },
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    hideDropdown() {
      this.openDropdown = false;
    },
    getRewardPrizes() {
      this.loading = true;

      this.$misc
        .get(`rewards-inventory`)
        .then(response => {
          this.rewardPrizes = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch(err => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
